import { useMsal } from '@azure/msal-react';
import React, { useContext, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { AmplitudeEventType } from '../../../../analytics/amplitudeEvents';
import { ClientConfig } from '../../../../clientConfig';
import { AuthorizationStatusType } from '../../../../router/types';
import { InstantActivationEvidence } from '../../../../types/authorizationEvidence';
import { ClientConfigContext } from '../../../clientConfigContext';
import { useBrand } from '../../../hooks/useBrand';
import { useMyChartRedirect } from '../../../hooks/useMyChartRedirect';
import { StateQueryParam, useQueryParams } from '../../../hooks/useQueryParams';
import { useValidatedUrl } from '../../../hooks/useValidatedUrl';
import { buildAIAEventProps, sendEvent, setUserId } from '../../../services/amplitudeClient';
import { activateMyChart, getAutoInstantActivationAuthorization } from '../../../services/pims/pimsClient';
import { usePIMS } from '../../../services/pims/usePIMS';
import { ErrorRedirect, useErrorHistoryPush } from '../../../util/errorUtils';
import getAccessToken from '../../../util/getAccessToken';
import { checkSessionAndNavigateToPromotionPage, navigateToPromotionPage, showAppPromotion } from '../../../util/promotionUtils';
import { redirectWithSoftLogout } from '../../../util/sessionTimeout';
import BrandedCard, { BrandedCardActions, BrandedCardContent, BrandedCardHeader } from '../../containers/BrandedCard_v2';
import CustomerServiceCard from '../../containers/CustomerServiceCard';
import Button from '../../elements/Button';
import withLogger, { ComponentLoggerProps } from '../../HOC/WithLogger';
import { GeneralFormProps } from '../types';
import AutoInstantActivationCardHeader from './AutoInstantActivationCardHeader';
import { ERROR_PAGE } from './AutoInstantActivationLandingPage';
import CollectDemographicsForm from './CollectDemographicsForm';

type AutoInstantActivationDemographics = {
    birthdate: string;
};

/**
 * State machine:
 * ValidatingState => DemographicsForm => Authorizing
 */
enum PageState {
    ValidatingState,
    DemographicsForm,
    Authorizing,
}

type AIAStateParams = {
    client_id: string;
    ehrSystem: string;
    instantActivationCode: string;
    isNonPatientProxy?: boolean;
    apiDepCondition?: string;
};

export const AutoInstantActivationAuthorizationPage = ({ log }: ComponentLoggerProps) => {
    const history = useHistory();
    const { instance, accounts } = useMsal();
    const [pageState, setPageState] = useState<PageState>(PageState.ValidatingState);
    const { brand, promotion } = useBrand();
    const { getSamlSsoPage, getLandingPage } = useMyChartRedirect();
    const { handleSubmit, errors, control, formState } = useForm({
        mode: 'onChange',
    });
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const validateRedirectUrl = useValidatedUrl();
    const { t } = useTranslation();
    const errorHistoryPush = useErrorHistoryPush();
    const { authorizeByInstantActivation } = usePIMS();
    const { queryParams, error } = useQueryParams<StateQueryParam, AIAStateParams>({
        requiredParams: ['state'],
    });

    /**
     * SPI-1801: Used to test the API Deprecation
     * TO-DO: Remove before production
     */
    const [apiDepCondition, setApiDepCondition] = useState<string | undefined>(undefined);
    useEffect(() => {
        setApiDepCondition(queryParams?.apiDepCondition);
    }, [queryParams?.apiDepCondition]);

    const { ehrSystem = '', isNonPatientProxy: isNonPatientProxyParam, instantActivationCode: oidcStateToken = '', client_id: clientId = '' } = queryParams ?? {};

    const errorPage = `${ERROR_PAGE}&brand=${brand}`;
    const contactSupportPage = `contact_support_error?brand=${brand}`;
    const isNonPatientProxy = isNonPatientProxyParam ?? false;

    // Get Azure User Id
    const tokenClaims = accounts[0]?.idTokenClaims;
    const signInName = tokenClaims?.signInName as string;
    const userId = tokenClaims?.sub as string;

    const { isDirty, isValid } = formState;

    // If the user cancels on the AIA flow, redirect them to the MyChart landing page
    const handleCancel = () => {
        const cancelUrl = getLandingPage(ehrSystem);
        redirectWithSoftLogout(instance, cancelUrl, validateRedirectUrl);
    };

    /**
     * Get the aia code from the session storage, or if that does not exist, fetch via the cache.
     * Returns undefined if neither sources finds a token.
     */
    const fetchInstantActivationCode = async (oidcStateToken: string, jwt: string): Promise<string | null> => {
        let aiaCode = sessionStorage.getItem('aiaCode');
        if (!aiaCode) {
            const userAgent = window.navigator.userAgent;
            log.warn('Cannot access session storage for temp token. Fetching from server', {
                details: userAgent.substring(0, 99), // length restriction
            }); // need to capture the user agents that are failing to fetch from the sessionStorage.

            const tokenParam = new URLSearchParams({ oidcStateToken });
            try {
                const response = await fetch('/instantactivation/code?' + tokenParam.toString(), {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + jwt,
                    },
                });
                const result = await response.json();
                aiaCode = result.aiaCode;
            } catch (error: any) {
                log.warn(`failed /instantactivation/code request: ${error.message}`);
            }
        }
        return aiaCode;
    };

    /**
     * Runs on page load.
     * Checks to see if the user is already authorized (with the correct EPI)
     */
    const getAuthorization = async () => {
        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log);
        const instantActivationCode = await fetchInstantActivationCode(oidcStateToken, jwt);
        if (!instantActivationCode) {
            errorHistoryPush(contactSupportPage, 'Cannot obtain original aia code');
            return;
        }
        const response = await getAutoInstantActivationAuthorization(jwt, clientConfiguration.pimsApiUrl, ehrSystem, instantActivationCode);

        switch (response.aiaCodeAuthorizationStatus) {
            case AuthorizationStatusType.Authorized: {
                const result = await activateMyChart(jwt, clientConfiguration.pimsApiUrl, ehrSystem);
                if (!result.wasSuccessful) {
                    if (result.error) {
                        log.error(`AutoInstantActivation(): Unable to reactivate MyChart. Error: ${result.error}`);
                    } else {
                        log.warn(`AutoInstantActivation(): Unable to reactivate MyChart`);
                    }
                }
                const myChartUrl = getSamlSsoPage(ehrSystem);
                if (!myChartUrl) {
                    errorHistoryPush(errorPage, 'No MyChart URL found for the authorized system');
                }
                if (promotion && showAppPromotion(promotion)) {
                    navigateToPromotionPage(history, myChartUrl);
                } else {
                    redirectWithSoftLogout(instance, myChartUrl, validateRedirectUrl);
                }
                break;
            }
            case AuthorizationStatusType.Unauthorized: {
                //logger.info(`aiaRedirect(): Record with healthSystemId: ${state.healthSystemId} is unAuthorized for ehrSystem: ${state.ehrSystem} redirecting to demographics page`);
                setPageState(PageState.DemographicsForm);
                break;
            }
            default:
                if (isNonPatientProxy) {
                    errorHistoryPush(contactSupportPage, 'An error occured in the authorization lookup for non patient proxy');
                } else {
                    errorHistoryPush(errorPage, 'An error occured in the authorization lookup for patient');
                }
                break;
        }
    };

    // Authorize the user by activation code and DOB demographics match
    const onSubmit: SubmitHandler<AutoInstantActivationDemographics> = async (data) => {
        setPageState(PageState.Authorizing);

        const jwt = await getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log);
        const eventProps = buildAIAEventProps(AmplitudeEventType.SSOSignUp_AIA_RecordMatch_Info_Next);
        sendEvent(eventProps, jwt);

        const instantActivationCode = await fetchInstantActivationCode(oidcStateToken, jwt);
        if (!instantActivationCode) {
            errorHistoryPush(contactSupportPage, 'Cannot obtain original aia code');
            return;
        }

        const authorizationEvidence: InstantActivationEvidence = {
            instantActivationCode: instantActivationCode,
            dateOfBirth: data.birthdate,
        };

        const response = await authorizeByInstantActivation({
            authorizationEvidence,
            ehrSystem,
            brand,
            clientId,
            apiDepCondition: apiDepCondition,
        });

        if (response.ok) {
            const eventProps = buildAIAEventProps(AmplitudeEventType.SSOSignUp_AIA_RecordMatch_Success);
            sendEvent(eventProps, jwt);
            let authorizedSystem = '';
            try {
                authorizedSystem = (await response.json()).authorizedSystem;
            } catch (err: any) {
                errorHistoryPush(errorPage, `Could not parse JSON response. ${err.message}`);
                return;
            }

            const myChartUrl = getSamlSsoPage(ehrSystem);
            if (!myChartUrl) {
                errorHistoryPush(errorPage, `Invalid epic configuration, no mychart url found for ${authorizedSystem} ${brand}`);
            } else {
                if (promotion && showAppPromotion(promotion)) {
                    navigateToPromotionPage(history, myChartUrl);
                } else {
                    redirectWithSoftLogout(instance, myChartUrl, validateRedirectUrl);
                }
            }
        } else {
            // Non Patient Proxy Demographics Validation Failure Should not go to Prove Flow instead go to Contact Support Page.
            log.error(
                `AutoInstantActivation(): NonPatientProxy :${isNonPatientProxy} : Error occurred - ${response.status}${response.statusText ? ` -  ${response.statusText}` : ''}`,
            );
            if (isNonPatientProxy && response.status === 400) {
                errorHistoryPush(contactSupportPage, 'Non Patient Proxy Demographics Validation Failure');
            } else if (response.status === 400) {
                errorHistoryPush(errorPage, 'Patient Demographics Validation Failure');
            } else {
                errorHistoryPush(errorPage, 'Error occurred in the authorization process');
            }
        }
    };

    useEffect(() => {
        if (error !== undefined || !ehrSystem || !signInName || !userId || !oidcStateToken) {
            return;
        }

        // If the user is returning here via the back button from an app store via App Promotion, then redirect them to the promotion page
        if (promotion && showAppPromotion(promotion)) {
            const redirectUrl = getSamlSsoPage(ehrSystem);
            checkSessionAndNavigateToPromotionPage(history, redirectUrl);
        }

        // ???
        window.scrollTo(0, 0);

        // Amplitude event: SSO - SignUp - AIA - Record Match - Info
        setUserId(userId);
        getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log).then((jwt) => {
            const eventProps = buildAIAEventProps(AmplitudeEventType.SSOSignUp_AIA_RecordMatch_Info);
            sendEvent(eventProps, jwt);
        });

        // Determine if the user is already authorized and can skip this page and proceed to MyChart
        getAuthorization();
    }, []);

    if (error !== undefined) {
        log.error(`AutoInstantActivationAuthorizationPage(): Error occurred - ${error}`);
        return <CustomerServiceCard />;
    }

    if (!ehrSystem || !signInName || !userId || !oidcStateToken) {
        log.warn('AutoInstantActivation(): no ehrSystem, signInName, userId or instantActivationCode found');
        return <ErrorRedirect to={errorPage} message='no ehrSystem, signInName, userId or instantActivationCode found' />;
    }

    return (
        <AutoInstantActivationAuthorizationCard
            control={control}
            errors={errors}
            pageState={pageState}
            handleCancel={handleCancel}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            t={t}
            isDirty={isDirty}
            isValid={isValid}
        />
    );
};

export function AutoInstantActivationAuthorizationCard({
    pageState,
    control,
    errors,
    handleCancel,
    handleSubmit,
    onSubmit,
    t,
    isDirty,
    isValid,
}: GeneralFormProps & {
    pageState: PageState;
    handleCancel: () => void;
    handleSubmit: (onSubmit: any) => void;
    onSubmit: (data: any) => void;
    t: any;
    isDirty: boolean;
    isValid: boolean;
}): JSX.Element {
    return (
        <BrandedCard maxWidth={450} minHeight={530} isLoading={pageState === PageState.Authorizing || pageState === PageState.ValidatingState}>
            <BrandedCardHeader>
                <AutoInstantActivationCardHeader loading={pageState === PageState.Authorizing} />
            </BrandedCardHeader>
            <BrandedCardContent>
                <CollectDemographicsForm control={control} errors={errors} infoComponent='' />
            </BrandedCardContent>
            <BrandedCardActions>
                <Button variant='text' type='button' onClick={handleCancel}>
                    {t('Cancel')}
                </Button>
                <Button disabled={!isDirty || !isValid} type='button' variant='contained' onClick={handleSubmit(onSubmit)}>
                    {t('Next')}
                </Button>
            </BrandedCardActions>
        </BrandedCard>
    );
}

export default withLogger(AutoInstantActivationAuthorizationPage, {
    eventSource: 'AutoInstantActivationAuthorizationPage',
});
